import React, { useState, useCallback, useEffect } from 'react';

const ClaraCategorizationIframTest = () => {
  // Viewport size presets
  const viewportPresets = {
    current: { label: 'Current (Responsive)', width: '100%', height: 'auto' },
    ultrawide: { label: 'Ultra Wide', width: '981px', height: '407px' },
    iphone14ProMax: { label: 'iPhone 14 Pro Max', width: '430px', height: '932px' },
    ipadPro: { label: 'iPad Pro 11"', width: '834px', height: '750px' },
  };

  // State for viewport size
  const [viewportSize, setViewportSize] = useState('current');

  // State for breakdown chart controls
  const [breakdownId, setBreakdownId] = useState('');
  const [breakdownSource, setBreakdownSource] = useState('wtw-clara');
  const [breakdownEnvType, setBreakdownEnvType] = useState('1');
  const [breakdownDarkMode, setBreakdownDarkMode] = useState(false);
  const [breakdownUrl, setBreakdownUrl] = useState('');

  // State for trends chart controls
  const [trendsId, setTrendsId] = useState('');
  const [trendsSource, setTrendsSource] = useState('wtw-clara');
  const [trendsEnvType, setTrendsEnvType] = useState('1');
  const [trendsDarkMode, setTrendsDarkMode] = useState(false);
  const [trendsUrl, setTrendsUrl] = useState('');

  // Base URL configuration
  const getBaseUrl = useCallback(() => {
    const basePath = process.env.REACT_APP_BASE_PATH || "/";
    // Normalize: if root path, return empty string; otherwise remove trailing slash
    const normalizedBasePath = basePath === "/" ? "" : basePath.replace(/\/$/, '');
    return window.location.origin + normalizedBasePath;
  }, []);

  // Generate breakdown iframe URL
  const generateBreakdownUrl = useCallback(() => {
    const baseUrl = getBaseUrl();
    let endpoint = '/iframe/clara-feedback';
    
    if (breakdownEnvType === '0') {
      endpoint = '/iframe/clara-feedback-test';
    } else if (breakdownEnvType === '2') {
      endpoint = '/iframe/clara-feedback-dev';
    }
    
    // Build query params - only include id if not empty
    const params = new URLSearchParams();
    if (breakdownId) {
      params.append('id', breakdownId);
    }
    params.append('source', breakdownSource);
    if (breakdownDarkMode) {
      params.append('darkMode', 'true');
    }
    
    return `${baseUrl}${endpoint}?${params.toString()}`;
  }, [getBaseUrl, breakdownEnvType, breakdownId, breakdownSource, breakdownDarkMode]);

  // Generate trends iframe URL
  const generateTrendsUrl = useCallback(() => {
    const baseUrl = getBaseUrl();
    let endpoint = '/iframe/clara-feedback-trends';
    
    if (trendsEnvType === '0') {
      endpoint = '/iframe/clara-feedback-trends-test';
    } else if (trendsEnvType === '2') {
      endpoint = '/iframe/clara-feedback-trends-dev';
    }
    
    // Build query params - only include id if not empty
    const params = new URLSearchParams();
    if (trendsId) {
      params.append('id', trendsId);
    }
    params.append('source', trendsSource);
    if (trendsDarkMode) {
      params.append('darkMode', 'true');
    }
    
    return `${baseUrl}${endpoint}?${params.toString()}`;
  }, [getBaseUrl, trendsEnvType, trendsId, trendsSource, trendsDarkMode]);

  // Load breakdown iframe
  const loadBreakdownIframe = () => {
    const url = generateBreakdownUrl();
    setBreakdownUrl(url);
    // Force iframe reload by updating key
    const iframe = document.getElementById('breakdownIframe');
    if (iframe) {
      iframe.src = url;
    }
  };

  // Load trends iframe
  const loadTrendsIframe = () => {
    const url = generateTrendsUrl();
    setTrendsUrl(url);
    // Force iframe reload by updating key
    const iframe = document.getElementById('trendsIframe');
    if (iframe) {
      iframe.src = url;
    }
  };

  // Auto-load both charts on component mount
  useEffect(() => {
    const breakdownUrlOnMount = generateBreakdownUrl();
    const trendsUrlOnMount = generateTrendsUrl();
    setBreakdownUrl(breakdownUrlOnMount);
    setTrendsUrl(trendsUrlOnMount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array means this runs once on mount

  // Get iframe container styles based on selected viewport
  const getIframeContainerStyle = () => {
    const preset = viewportPresets[viewportSize];
    if (viewportSize === 'current') {
      return { width: '100%' };
    }
    
    return {
      width: preset.width,
      maxWidth: '100%',
      margin: '0 auto',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
    };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="flex justify-between items-center p-4 border-b bg-white shadow-sm">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Iframe Test Page</h1>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Viewport Size:</label>
          <select
            value={viewportSize}
            onChange={(e) => setViewportSize(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            {Object.entries(viewportPresets).map(([key, preset]) => (
              <option key={key} value={key}>
                {preset.label}
                {key !== 'current' && ` (${preset.width} × ${preset.height})`}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Breakdown Chart Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">
            Breakdown Chart (Pie Chart)
          </h2>
          
          {/* Test Controls */}
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">ID:</label>
                <input
                  type="number"
                  value={breakdownId}
                  onChange={(e) => setBreakdownId(e.target.value)}
                  placeholder="All IDs"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Source:</label>
                <select
                  value={breakdownSource}
                  onChange={(e) => setBreakdownSource(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="invite-clara">Invite Clara</option>
                  <option value="wtw-clara">WTW Clara</option>
                  <option value="wtw-gdoc-clara">WTW GDoc Clara</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Environment:</label>
                <select
                  value={breakdownEnvType}
                  onChange={(e) => setBreakdownEnvType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="1">Production</option>
                  <option value="0">Test</option>
                  <option value="2">Development</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={breakdownDarkMode}
                    onChange={(e) => setBreakdownDarkMode(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Dark Mode</span>
                </label>
              </div>
              
              <button
                onClick={loadBreakdownIframe}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Load Chart
              </button>
            </div>
            
            <div className="mt-3">
              <p className="text-xs text-gray-600 mb-1">Current URL:</p>
              <code className="bg-gray-100 px-2 py-1 rounded text-xs break-all">
                {breakdownUrl}
              </code>
            </div>
          </div>

          {/* Iframe Container */}
          <div style={getIframeContainerStyle()}>
            <iframe
              id="breakdownIframe"
              src={breakdownUrl}
              width={viewportSize === 'current' ? '100%' : viewportPresets[viewportSize].width}
              height={viewportSize === 'current' ? '700' : viewportPresets[viewportSize].height}
              className="border-0"
              style={{ display: 'block' }}
              title="Clara Feedback Breakdown Chart"
            />
          </div>
        </div>

        {/* Trends Chart Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">
            Trends Chart (Stream Chart)
          </h2>
          
          {/* Test Controls */}
          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">ID:</label>
                <input
                  type="number"
                  value={trendsId}
                  onChange={(e) => setTrendsId(e.target.value)}
                  placeholder="All IDs"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Source:</label>
                <select
                  value={trendsSource}
                  onChange={(e) => setTrendsSource(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="invite-clara">Invite Clara</option>
                  <option value="wtw-clara">WTW Clara</option>
                  <option value="wtw-gdoc-clara">WTW GDoc Clara</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Environment:</label>
                <select
                  value={trendsEnvType}
                  onChange={(e) => setTrendsEnvType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="1">Production</option>
                  <option value="0">Test</option>
                  <option value="2">Development</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={trendsDarkMode}
                    onChange={(e) => setTrendsDarkMode(e.target.checked)}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Dark Mode</span>
                </label>
              </div>
              
              <button
                onClick={loadTrendsIframe}
                className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
              >
                Load Trends Chart
              </button>
            </div>
            
            <div className="mt-3">
              <p className="text-xs text-gray-600 mb-1">Current URL:</p>
              <code className="bg-gray-100 px-2 py-1 rounded text-xs break-all">
                {trendsUrl}
              </code>
            </div>
          </div>

          {/* Iframe Container */}
          <div style={getIframeContainerStyle()}>
            <iframe
              id="trendsIframe"
              src={trendsUrl}
              width={viewportSize === 'current' ? '100%' : viewportPresets[viewportSize].width}
              height={viewportSize === 'current' ? '800' : viewportPresets[viewportSize].height}
              className="border-0"
              style={{ display: 'block' }}
              title="Clara Feedback Trends Chart"
            />
          </div>
        </div>

        
      </div>
    </div>
  );
};

export default ClaraCategorizationIframTest;